import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Gift, Users, User, Calendar, Tag, CheckCircle, XCircle, Smartphone, Star, StarOff } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PromoCodesPage = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const navigate = useNavigate();

  const [newCode, setNewCode] = useState({
    code: '',
    tour_id: '',
    discount_type: 'free',
    discount_percent: 100,
    free_devices: 3,
    device_discount_percent: 50,
    usage_type: 'single',
    max_uses: 1,
    expires_at: '',
    featured: false,
    headline: ''
  });

  useEffect(() => {
    fetchPromoCodes();
    fetchTours();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      const response = await axios.get(`${API}/admin/promo-codes`);
      setPromoCodes(response.data);
    } catch (error) {
      toast.error('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  const fetchTours = async () => {
    try {
      const response = await axios.get(`${API}/tours`);
      setTours(response.data);
    } catch (error) {
      console.error('Failed to load tours');
    }
  };

  const handleCreateCode = async (e) => {
    e.preventDefault();
    
    if (!newCode.code.trim()) {
      toast.error('Please enter a promo code');
      return;
    }
    
    if (!newCode.expires_at) {
      toast.error('Please set an expiration date');
      return;
    }

    try {
      // Parse the date and set to end of day (23:59:59)
      const [year, month, day] = newCode.expires_at.split('-').map(Number);
      const expiryDate = new Date(year, month - 1, day, 23, 59, 59);
      
      if (isNaN(expiryDate.getTime())) {
        toast.error('Invalid expiration date');
        return;
      }
      
      const payload = {
        ...newCode,
        tour_id: newCode.tour_id || null,
        expires_at: expiryDate.toISOString()
      };
      
      await axios.post(`${API}/admin/promo-codes`, payload);
      toast.success('Promo code created successfully');
      setShowCreateModal(false);
      setNewCode({
        code: '',
        tour_id: '',
        discount_type: 'free',
        discount_percent: 100,
        free_devices: 3,
        device_discount_percent: 50,
        usage_type: 'single',
        max_uses: 1,
        expires_at: '',
        featured: false,
        headline: ''
      });
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promo code');
    }
  };

  const handleDeleteCode = async (promoId, code) => {
    const confirmed = window.confirm(`Are you sure you want to delete promo code "${code}"?`);
    if (!confirmed) return;

    try {
      await axios.delete(`${API}/admin/promo-codes/${promoId}`);
      toast.success('Promo code deleted');
      fetchPromoCodes();    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  const isFullyUsed = (promo) => {
    if (promo.usage_type === 'single' && promo.times_used >= 1) return true;
    if (promo.usage_type === 'multi' && promo.max_uses && promo.times_used >= promo.max_uses) return true;
    return false;
  };

  const [bannerEditor, setBannerEditor] = useState(null);
  // bannerEditor shape: { promo, form: { featured, headline, banner_tag, banner_subtext, banner_cta } }

  const openBannerEditor = (promo, turningOn) => {
    setBannerEditor({
      promo,
      turningOn,
      form: {
        featured: turningOn !== undefined ? turningOn : !promo.featured,
        headline: promo.headline || '',
        banner_tag: promo.banner_tag || '',
        banner_subtext: promo.banner_subtext || '',
        banner_cta: promo.banner_cta || '',
      },
    });
  };

  const toggleFeatured = async (promo) => {
    // If we're turning it OFF, just do it directly without a modal.
    if (promo.featured) {
      try {
        await axios.put(`${API}/admin/promo-codes/${promo.promo_id}/feature`, {
          featured: false,
        });
        toast.success('Promo unfeatured');
        fetchPromoCodes();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update featured state');
      }
      return;
    }
    // Turning ON → open the banner content editor.
    openBannerEditor(promo, true);
  };

  const saveBannerEditor = async () => {
    if (!bannerEditor) return;
    const { promo, form } = bannerEditor;
    try {
      await axios.put(`${API}/admin/promo-codes/${promo.promo_id}/feature`, form);
      toast.success(form.featured ? 'Banner saved & promo featured' : 'Banner saved');
      setBannerEditor(null);
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save banner');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center text-slate-300 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
          <div className="flex items-center mb-4">
            <Gift className="w-10 h-10 text-teal-500 mr-3" />
            <h1 className="text-4xl font-bold font-sans">Promo Codes</h1>
          </div>
          <p className="text-slate-300 text-lg font-body">Create and manage promotional codes for free or discounted tours</p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-bold text-slate-900 font-sans">All Promo Codes</h2>
          <button
            onClick={() => setShowCreateModal(true)}
            data-testid="create-promo-button"
            className="flex items-center bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Promo Code
          </button>
        </div>

        {promoCodes.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-card">
            <Gift className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600 text-lg mb-4 font-body">No promo codes created yet</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
            >
              Create Your First Promo Code
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {promoCodes.map((promo) => (
              <div
                key={promo.promo_id}
                data-testid={`promo-card-${promo.promo_id}`}
                className={`bg-white rounded-2xl shadow-card overflow-hidden ${
                  isExpired(promo.expires_at) || isFullyUsed(promo) ? 'opacity-60' : ''
                }`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className="bg-gradient-to-br from-teal-500 to-cyan-500 text-white px-4 py-2 rounded-lg font-mono font-bold text-xl mr-4">
                        {promo.code}
                      </div>
                      <div>
                        {isExpired(promo.expires_at) ? (
                          <span className="flex items-center px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm font-semibold">
                            <XCircle className="w-4 h-4 mr-1" />
                            Expired
                          </span>
                        ) : isFullyUsed(promo) ? (
                          <span className="flex items-center px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-sm font-semibold">
                            <XCircle className="w-4 h-4 mr-1" />
                            Fully Used
                          </span>
                        ) : (
                          <span className="flex items-center px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm font-semibold">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Active
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteCode(promo.promo_id, promo.code)}
                      data-testid={`delete-promo-${promo.promo_id}`}
                      className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Featured advertising indicator */}
                  {promo.featured && (
                    <div
                      data-testid={`featured-indicator-${promo.promo_id}`}
                      className="bg-amber-50 border-2 border-amber-300 text-amber-900 px-3 py-2 rounded-lg mb-3 text-sm"
                    >
                      <div className="flex items-center gap-2 font-semibold mb-1">
                        <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                        Featured on tour page
                      </div>
                      <div className="text-xs space-y-0.5">
                        {promo.banner_tag && <div><b>Tag:</b> {promo.banner_tag}</div>}
                        {promo.headline && <div><b>Headline:</b> "{promo.headline}"</div>}
                        {promo.banner_subtext && <div><b>Subtext:</b> {promo.banner_subtext}</div>}
                        {promo.banner_cta && <div><b>Button:</b> {promo.banner_cta}</div>}
                        {!promo.banner_tag && !promo.headline && !promo.banner_subtext && !promo.banner_cta && (
                          <div className="italic opacity-80">Using default banner copy — click "Edit banner content" to customize.</div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wider font-body flex items-center">
                        <Tag className="w-3 h-3 mr-1" />
                        Type
                      </span>
                      <p className="text-slate-900 font-semibold font-body">
                        {promo.discount_type === 'free' && 'Free Tour (100% off)'}
                        {promo.discount_type === 'percent' && `${promo.discount_percent}% Off Tour`}
                        {promo.discount_type === 'free_devices' && `${promo.free_devices} Free Device(s)`}
                        {promo.discount_type === 'discount_devices' && `${promo.device_discount_percent}% Off Devices`}
                        {promo.discount_type === 'discount' && `${promo.discount_percent}% Off`}
                      </p>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wider font-body flex items-center">
                        {promo.usage_type === 'single' ? <User className="w-3 h-3 mr-1" /> : <Users className="w-3 h-3 mr-1" />}
                        Usage
                      </span>
                      <p className="text-slate-900 font-semibold font-body">
                        {promo.usage_type === 'single' ? 'Single Use' : `Multi (${promo.times_used}/${promo.max_uses || '∞'})`}
                      </p>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wider font-body flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        Expires
                      </span>
                      <p className="text-slate-900 font-semibold font-body">{formatDate(promo.expires_at)}</p>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wider font-body">Valid For</span>
                      <p className="text-slate-900 font-semibold font-body truncate">{promo.tour_title}</p>
                    </div>
                  </div>

                  {promo.redemptions && promo.redemptions.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <p className="text-slate-500 text-xs uppercase tracking-wider font-body mb-2">
                        Redemptions ({promo.redemptions.length})
                      </p>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {promo.redemptions.map((r, idx) => (
                          <div key={idx} className="text-sm text-slate-600 font-body flex justify-between">
                            <span>{r.first_name} {r.last_name} ({r.email})</span>
                            <span className="text-slate-400">{formatDate(r.redeemed_at)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-slate-200 flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => toggleFeatured(promo)}
                      data-testid={`toggle-featured-${promo.promo_id}`}
                      disabled={isExpired(promo.expires_at) || isFullyUsed(promo)}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                        promo.featured
                          ? 'bg-amber-500 text-white hover:bg-amber-600'
                          : 'bg-slate-100 text-slate-700 hover:bg-amber-100 hover:text-amber-800 border border-slate-200'
                      }`}
                      title="Show this code as an advertised banner on the tour page"
                    >
                      {promo.featured ? (
                        <>
                          <StarOff className="w-4 h-4" />
                          Unfeature
                        </>
                      ) : (
                        <>
                          <Star className="w-4 h-4" />
                          Feature on tour page
                        </>
                      )}
                    </button>

                    {promo.featured && (
                      <button
                        type="button"
                        onClick={() => openBannerEditor(promo, true)}
                        data-testid={`edit-banner-${promo.promo_id}`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 transition-all"
                      >
                        <Tag className="w-4 h-4" />
                        Edit banner content
                      </button>
                    )}

                    {promo.featured && (
                      <button
                        type="button"
                        onClick={() => {
                          const base = window.location.origin;
                          const path = promo.tour_id
                            ? `/tours/${promo.tour_id}?promo=${promo.code}`
                            : `/?promo=${promo.code}`;
                          const url = `${base}${path}`;
                          navigator.clipboard
                            .writeText(url)
                            .then(() => toast.success(`Ad link copied: ${url}`))
                            .catch(() => {
                              window.prompt('Copy this link:', url);
                            });
                        }}
                        data-testid={`copy-ad-link-${promo.promo_id}`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 transition-all"
                      >
                        <Tag className="w-4 h-4" />
                        Copy shareable ad link
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create Promo Code Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-floating max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-900 font-sans">Create Promo Code</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleCreateCode} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">Promo Code</label>
                  <input
                    type="text"
                    value={newCode.code}
                    onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                    data-testid="promo-code-input"
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-mono uppercase"
                    placeholder="e.g., WELCOME2025"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">Valid For Tour</label>
                  <select
                    value={newCode.tour_id}
                    onChange={(e) => setNewCode({ ...newCode, tour_id: e.target.value })}
                    data-testid="promo-tour-select"
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-body"
                  >
                    <option value="">All Tours</option>
                    {tours.map((tour) => (
                      <option key={tour.tour_id} value={tour.tour_id}>{tour.title}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">Discount Type</label>
                  <div className="space-y-3">
                    <label className="flex items-center cursor-pointer p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                      <input
                        type="radio"
                        name="discount_type"
                        value="free"
                        checked={newCode.discount_type === 'free'}
                        onChange={(e) => setNewCode({ ...newCode, discount_type: e.target.value })}
                        className="mr-3"
                      />
                      <div>
                        <span className="font-semibold font-body text-slate-900">Free Tour</span>
                        <p className="text-sm text-slate-500">100% off the tour price</p>
                      </div>
                    </label>
                    <label className="flex items-center cursor-pointer p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                      <input
                        type="radio"
                        name="discount_type"
                        value="percent"
                        checked={newCode.discount_type === 'percent'}
                        onChange={(e) => setNewCode({ ...newCode, discount_type: e.target.value })}
                        className="mr-3"
                      />
                      <div>
                        <span className="font-semibold font-body text-slate-900">Percentage Off Tour</span>
                        <p className="text-sm text-slate-500">Custom % discount on tour price</p>
                      </div>
                    </label>
                    <label className="flex items-center cursor-pointer p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                      <input
                        type="radio"
                        name="discount_type"
                        value="free_devices"
                        checked={newCode.discount_type === 'free_devices'}
                        onChange={(e) => setNewCode({ ...newCode, discount_type: e.target.value })}
                        className="mr-3"
                      />
                      <div>
                        <span className="font-semibold font-body text-slate-900">Free Additional Devices</span>
                        <p className="text-sm text-slate-500">Give free companion passes</p>
                      </div>
                    </label>
                    <label className="flex items-center cursor-pointer p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                      <input
                        type="radio"
                        name="discount_type"
                        value="discount_devices"
                        checked={newCode.discount_type === 'discount_devices'}
                        onChange={(e) => setNewCode({ ...newCode, discount_type: e.target.value })}
                        className="mr-3"
                      />
                      <div>
                        <span className="font-semibold font-body text-slate-900">Discounted Devices</span>
                        <p className="text-sm text-slate-500">% off additional device price</p>
                      </div>
                    </label>
                  </div>
                </div>

                {newCode.discount_type === 'percent' && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">Tour Discount Percentage</label>
                    <div className="flex items-center">
                      <input
                        type="number"
                        min="1"
                        max="99"
                        value={newCode.discount_percent}
                        onChange={(e) => setNewCode({ ...newCode, discount_percent: parseInt(e.target.value) || 0 })}
                        data-testid="promo-discount-input"
                        className="w-24 px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-body"
                      />
                      <span className="ml-2 text-slate-600 font-semibold">% off tour price</span>
                    </div>
                  </div>
                )}

                {newCode.discount_type === 'free_devices' && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">Number of Free Devices</label>
                    <div className="flex items-center">
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={newCode.free_devices}
                        onChange={(e) => setNewCode({ ...newCode, free_devices: parseInt(e.target.value) || 1 })}
                        data-testid="promo-free-devices-input"
                        className="w-24 px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-body"
                      />
                      <span className="ml-2 text-slate-600 font-semibold">free companion device(s)</span>
                    </div>
                  </div>
                )}

                {newCode.discount_type === 'discount_devices' && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">Device Discount Percentage</label>
                    <div className="flex items-center">
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={newCode.device_discount_percent}
                        onChange={(e) => setNewCode({ ...newCode, device_discount_percent: parseInt(e.target.value) || 0 })}
                        data-testid="promo-device-discount-input"
                        className="w-24 px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-body"
                      />
                      <span className="ml-2 text-slate-600 font-semibold">% off device price</span>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">Usage Type</label>
                  <div className="flex gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="usage_type"
                        value="single"
                        checked={newCode.usage_type === 'single'}
                        onChange={(e) => setNewCode({ ...newCode, usage_type: e.target.value, max_uses: 1 })}
                        className="mr-2"
                      />
                      <span className="font-body">Single Use (1 person)</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="usage_type"
                        value="multi"
                        checked={newCode.usage_type === 'multi'}
                        onChange={(e) => setNewCode({ ...newCode, usage_type: e.target.value })}
                        className="mr-2"
                      />
                      <span className="font-body">Multi-Use (many people)</span>
                    </label>
                  </div>
                </div>

                {newCode.usage_type === 'multi' && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">Maximum Uses</label>
                    <input
                      type="number"
                      min="2"
                      value={newCode.max_uses}
                      onChange={(e) => setNewCode({ ...newCode, max_uses: parseInt(e.target.value) || 2 })}
                      data-testid="promo-max-uses-input"
                      className="w-24 px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-body"
                    />
                    <p className="text-sm text-slate-500 mt-1 font-body">Leave empty for unlimited uses</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2 font-body">Expiration Date</label>
                  <input
                    type="date"
                    value={newCode.expires_at}
                    onChange={(e) => setNewCode({ ...newCode, expires_at: e.target.value })}
                    data-testid="promo-expiry-input"
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-body"
                  />
                  <p className="text-xs text-slate-500 mt-1">Code expires at end of selected day (11:59 PM)</p>
                </div>

                {/* Advertise as featured banner */}
                <div className="p-4 rounded-xl bg-amber-50 border-2 border-amber-200">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newCode.featured}
                      onChange={(e) => setNewCode({ ...newCode, featured: e.target.checked })}
                      data-testid="promo-featured-checkbox"
                      className="mt-1 w-5 h-5"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                        <span className="font-bold text-slate-900 font-sans">Advertise on tour page</span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">
                        Shows an eye-catching banner with this code on the tour detail page (and tour cards)
                        so visitors and ad traffic can spot it instantly.
                      </p>
                    </div>
                  </label>
                  {newCode.featured && (
                    <div className="mt-3">
                      <label className="block text-sm font-semibold text-slate-700 mb-1 font-body">
                        Banner headline (optional)
                      </label>
                      <input
                        type="text"
                        value={newCode.headline}
                        onChange={(e) => setNewCode({ ...newCode, headline: e.target.value })}
                        placeholder="e.g. Free this week — first 100 explorers!"
                        maxLength={120}
                        data-testid="promo-headline-input"
                        className="w-full px-4 py-3 rounded-lg border border-amber-300 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 font-body"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Leave blank for an automatic headline.
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-700 py-3 rounded-full font-semibold transition-all active:scale-95 font-sans"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    data-testid="submit-promo-button"
                    className="flex-1 bg-teal-500 hover:bg-teal-600 text-white py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
                  >
                    Create Code
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Banner Content Editor Modal */}
      {bannerEditor && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="banner-editor-modal">
          <div className="bg-white rounded-2xl max-w-xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
            <div className="p-6 border-b border-slate-200 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 font-sans flex items-center gap-2">
                  <Star className="w-6 h-6 fill-amber-400 text-amber-400" />
                  Banner Content
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  Customize exactly what visitors see on the tour page for{' '}
                  <code className="bg-slate-100 px-2 py-0.5 rounded font-mono font-bold">
                    {bannerEditor.promo.code}
                  </code>
                </p>
              </div>
              <button
                onClick={() => setBannerEditor(null)}
                className="text-slate-400 hover:text-slate-700 p-1"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Live preview strip */}
              <div className="rounded-xl overflow-hidden border-2 border-amber-300">
                <div className="bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 text-white p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <div className="bg-white text-orange-600 font-black uppercase tracking-wider text-xs px-3 py-1 rounded-full shrink-0">
                    {bannerEditor.form.banner_tag?.trim() ||
                      (bannerEditor.promo.discount_type === 'free' || bannerEditor.promo.discount_percent === 100
                        ? 'FREE'
                        : `${bannerEditor.promo.discount_percent}% OFF`)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-base sm:text-lg leading-snug" data-testid="banner-preview-headline">
                      {bannerEditor.form.headline?.trim() ||
                        (bannerEditor.promo.discount_type === 'free' || bannerEditor.promo.discount_percent === 100
                          ? 'This tour is FREE with code below'
                          : `Unlock ${bannerEditor.promo.discount_percent}% off with the code below`)}
                    </p>
                    {bannerEditor.form.banner_subtext?.trim() && (
                      <p className="text-white/95 text-sm mt-0.5 font-semibold">
                        {bannerEditor.form.banner_subtext.trim()}
                      </p>
                    )}
                    <div className="mt-2 inline-block bg-white text-orange-600 font-mono font-black text-sm px-3 py-1 rounded-lg tracking-wider">
                      {bannerEditor.promo.code}
                    </div>
                  </div>
                  <button className="bg-white text-orange-600 font-black uppercase tracking-wide text-xs px-4 py-2 rounded-full shrink-0">
                    {bannerEditor.form.banner_cta?.trim() || 'Apply Code'}
                  </button>
                </div>
                <p className="text-xs text-slate-500 bg-slate-50 p-2 text-center">Live preview</p>
              </div>

              {/* Fields */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  Tag label <span className="text-slate-400 text-xs">(short pill text)</span>
                </label>
                <input
                  type="text"
                  value={bannerEditor.form.banner_tag}
                  onChange={(e) =>
                    setBannerEditor({ ...bannerEditor, form: { ...bannerEditor.form, banner_tag: e.target.value } })
                  }
                  maxLength={24}
                  placeholder="e.g. FREE, LIMITED, SUMMER SPECIAL"
                  data-testid="banner-tag-input"
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 font-body"
                />
                <p className="text-xs text-slate-500 mt-1">Blank = auto ("FREE" or "X% OFF").</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Headline</label>
                <input
                  type="text"
                  value={bannerEditor.form.headline}
                  onChange={(e) =>
                    setBannerEditor({ ...bannerEditor, form: { ...bannerEditor.form, headline: e.target.value } })
                  }
                  maxLength={120}
                  placeholder="e.g. Free this week — first 100 explorers!"
                  data-testid="banner-headline-input"
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  Sub-text <span className="text-slate-400 text-xs">(optional, smaller line under headline)</span>
                </label>
                <input
                  type="text"
                  value={bannerEditor.form.banner_subtext}
                  onChange={(e) =>
                    setBannerEditor({ ...bannerEditor, form: { ...bannerEditor.form, banner_subtext: e.target.value } })
                  }
                  maxLength={160}
                  placeholder="e.g. Tap below to unlock your free tour — no credit card needed"
                  data-testid="banner-subtext-input"
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  Button text <span className="text-slate-400 text-xs">(CTA)</span>
                </label>
                <input
                  type="text"
                  value={bannerEditor.form.banner_cta}
                  onChange={(e) =>
                    setBannerEditor({ ...bannerEditor, form: { ...bannerEditor.form, banner_cta: e.target.value } })
                  }
                  maxLength={32}
                  placeholder="Apply Code"
                  data-testid="banner-cta-input"
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 font-body"
                />
                <p className="text-xs text-slate-500 mt-1">Blank = "Apply Code".</p>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex gap-3">
              <button
                type="button"
                onClick={() => setBannerEditor(null)}
                className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-700 py-3 rounded-full font-semibold transition-all active:scale-95"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveBannerEditor}
                data-testid="save-banner-btn"
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95"
              >
                Save Banner
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoCodesPage;
